/* eslint-disable @typescript-eslint/no-explicit-any */
import { SelectionModel } from '@angular/cdk/collections';
import { EventEmitter } from '@angular/core';
import { FormArray, FormControlStatus, FormGroup } from '@angular/forms';

/**
 * A custom FormArray that integrates with a SelectionModel and provides only
 * status update for selected controls.
 *
 * @template T - A type that extends FormGroup.
 */
export class SelectionFormArray<T extends FormGroup> extends FormArray<T> {
  private selectionModel: SelectionModel<T>;

  constructor(controls: T[]) {
    super(controls);
    this.selectionModel = new SelectionModel<T>(true, []);
    this.selectionModel.changed.subscribe(() => {
      this.updateValueAndValidity({ emitEvent: true });
    });
  }

  /**
   * NOTE:  There are things we are doing in this method that are not recommended.
   *   We are setting the status and emitting events from the base AbstractControl
   *   that do not have proper accessor methods.
   */
  override updateValueAndValidity(opts: { onlySelf?: boolean; emitEvent?: boolean } = {}): void {
    const selectedControls = this.controls.filter((control) => this.selectionModel.isSelected(control as T));

    // Manually update the status of the FormArray
    if (selectedControls.every((control) => control.valid)) {
      (this as { status: FormControlStatus }).status = 'VALID';
    } else if (selectedControls.some((control) => control.pending)) {
      (this as { status: FormControlStatus }).status = 'PENDING';
    } else {
      (this as { status: FormControlStatus }).status = 'INVALID';
    }

    if (opts.emitEvent !== false) {
      (this.valueChanges as unknown as EventEmitter<any>).emit(this.value);
      (this.statusChanges as unknown as EventEmitter<any>).emit(this.status);
    }

    // Propagate the status change to the parent FormGroup
    if (this.parent) {
      Promise.resolve().then(() => {
        this.parent!.markAsDirty();
        this.parent!.updateValueAndValidity({ onlySelf: opts.onlySelf, emitEvent: opts.emitEvent });
      });
    }
  }

  // we can't override the base property `value`.. keep in mind using
  //  .value accessor will return all values, not just selected ones.
  getSelectedValue(): any {
    return this.controls
      .filter((control) => this.selectionModel.isSelected(control as T) && control.enabled)
      .map((control) => control.value);
  }

  /**
   * Gets the raw value of the selected controls.
   * @returns The raw value of the selected controls.
   */
  override getRawValue(): any {
    return this.controls.filter((control) => this.selectionModel.isSelected(control as T)).map((control) => control.getRawValue());
  }

  /**
   * Gets the selection model for the form array.
   *
   * @readonly
   * @type {SelectionModel<T>}
   */
  get selection(): SelectionModel<T> {
    return this.selectionModel;
  }
}
